<template>
  <div class="modern-container">
    <PageHeader
      title="Subir imágenes al feed"
      class="mb-4"
    />
    
    <b-card
      no-body
      class="modern-card"
    >
      <b-card-body class="modern-card-body">
        <b-form class="modern-form">
          <!-- Store Selector -->
          <store-selector
            v-if="!storeIdFromRoute"
            v-model="storeId"
            label="Tienda:"
            input-id="store-selector"
          />

          <!-- Category Selector -->
          <category-selector
            v-if="!categoryIdFromRoute"
            v-model="categoryId"
            label="ID de Categoría:"
            input-id="category-selector"
          />
        </b-form>

        <upload-images
          v-model="uploadData"
        />
      </b-card-body>
    </b-card>

    <div class="action-buttons">
      <b-button
        variant="outline-secondary"
        class="modern-btn"
        @click="goBack"
      >
        <i class="fa fa-arrow-left mr-2"></i>
        Volver
      </b-button>
      <b-button
        variant="primary"
        class="modern-btn"
        :disabled="!canSubmit || processing"
        @click="handleSubmit"
      >
        <b-spinner
          v-if="processing"
          small
          class="mr-2"
        />
        <i
          v-else
          class="fa fa-upload mr-2"
        />
        {{ processing ? 'Subiendo...' : 'Subir imágenes' }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import { trade } from '@/services/api/ExpertoRetail'
import PageHeader from '@/app/shared/components/PageHeader'
import UploadImages from './components/UploadImages'
import StoreSelector from './components/StoreSelector'
import CategorySelector from './components/CategorySelector'

export default {
  name: 'UploadImagesFeed',
  components: {
    PageHeader,
    UploadImages,
    StoreSelector,
    CategorySelector
  },
  setup (props, context) {
    const uploadData = ref({
      caption: '',
      images: []
    })
    const processing = ref(false)
    
    // Get storeId and categoryId from route params, query params, or vuex store
    const route = context.root.$route
    const store = context.root.$store

    // Determine if storeId and categoryId come from route params
    const storeIdFromRoute = route.params.storeId || route.query.storeId
    const categoryIdFromRoute = route.params.categoryId || route.query.categoryId

    const storeId = ref(storeIdFromRoute || (store.state.mobile && store.state.mobile.store_id ? store.state.mobile.store_id.toString() : null))
    const categoryId = ref(categoryIdFromRoute || null)

    const canSubmit = computed(() => {
      return uploadData.value.images.length > 0 && 
             storeId.value && 
             categoryId.value && 
             !processing.value
    })

    const makeToast = (body, title, variant = 'danger') => {
      context.root.$bvToast.toast(body, {
        title: title,
        autoHideDelay: 5000,
        appendToast: false,
        solid: true,
        variant: variant
      })
    }

    const goBack = () => {
      context.root.$router.go(-1)
    }

    const handleSubmit = async () => {
      if (!canSubmit.value) return

      // Validate required fields
      if (!storeId.value || !categoryId.value) {
        makeToast('Por favor ingresa el ID de tienda y categoría', 'Error')
        return
      }

      processing.value = true

      try {
        // Transform images to backend format: {mime: "image/png", img: "base64..."}
        const imgList = (uploadData.value.images || []).map(img => {
          let base64String = ''
          
          // Get the base64 string
          if (typeof img === 'string') {
            base64String = img
          } else if (img && img.base64) {
            base64String = img.base64
          } else {
            base64String = String(img)
          }
          
          // Extract MIME type and base64 data from data URL
          // Format: "data:image/jpeg;base64,/9j/4AAQ..."
          let mimeType = 'image/jpeg' // default
          let base64Data = base64String
          
          if (base64String.startsWith('data:')) {
            const matches = base64String.match(/^data:([^;]+);base64,(.+)$/)
            if (matches && matches.length === 3) {
              mimeType = matches[1] // e.g., "image/png", "image/jpeg"
              base64Data = matches[2] // base64 without prefix
            }
          }
          
          // Debug: log first image to verify format
          console.log('Image format:', {
            originalLength: base64String.length,
            mime: mimeType,
            base64Length: base64Data.length,
            base64Start: base64Data.substring(0, 50) + '...',
            hasDataPrefix: base64String.startsWith('data:')
          })
          
          return {
            mime: mimeType,
            img: base64Data
          }
        })

        // Create clean payload
        const cleanPayload = JSON.parse(JSON.stringify({
          feedData: {
            storeId: parseInt(storeId.value),
            categoryId: parseInt(categoryId.value),
            content: {
              caption: uploadData.value.caption || '',
              imgList: imgList
            }
          }
        }))
        
        console.log('Final payload imgList sample:', cleanPayload.feedData.content.imgList[0])
        
        await trade.postNewContentToFeed(cleanPayload)
        
        makeToast('Imágenes subidas exitosamente', 'Éxito', 'success')
        
        // Redirect back after success
        setTimeout(() => {
          context.root.$router.push({ name: 'StorePublicationContent' })
        }, 1500)
      } catch (error) {
        const errorMessage = error.message || 'Error al subir las imágenes'
        makeToast(errorMessage, 'Error')
      } finally {
        processing.value = false
      }
    }

    return {
      uploadData,
      processing,
      canSubmit,
      goBack,
      handleSubmit,
      storeId,
      categoryId,
      storeIdFromRoute,
      categoryIdFromRoute
    }
  }
}
</script>

<style scoped>
.modern-container {
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

.modern-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  overflow: hidden;
  margin-bottom: 2rem;
}

.modern-card-body {
  padding: 2rem;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
}

.modern-btn {
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.modern-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.modern-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .modern-container {
    padding: 1rem 0.5rem;
  }

  .modern-card-body {
    padding: 1rem;
  }

  .action-buttons {
    flex-direction: column;
  }

  .modern-btn {
    width: 100%;
  }
}
</style>




// WEBPACK FOOTER //
// src/expertoRetail/views/trade/UploadImagesFeed.vue